import { DrillDownDialog } from "@/components/accounting/drill-down-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import { useGetCrossTabBalance, useListAccounts } from "@/hooks/use-accounting";
import {
	type DimensionFilter,
	DimensionFilterBar,
} from "@/components/accounting/dimension-filter-bar";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { useAccountingContext } from "@/lib/accounting-context";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useGridLayout } from "@/lib/use-grid-layout";

import type { ColDef, ValueFormatterParams } from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { createFileRoute } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface CrossTabSearch {
	mode?: "chess" | "journal-order";
}

export const Route = createFileRoute("/accounting/crosstab")({
	component: CrossTabPage,
	validateSearch: (search: Record<string, unknown>): CrossTabSearch => ({
		mode:
			search.mode === "journal-order" ? "journal-order" : undefined,
	}),
});

function formatAmount(value: number): string {
	return new Intl.NumberFormat("ru-RU", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(value);
}

type CrossTabRow = Record<string, string | number>;

function CrossTabPage() {
	const { t } = useTranslation("accounting");
	const search = Route.useSearch();
	const gridRef = useRef<AgGridReact>(null);
	const { onGridReady, onStateChanged } = useGridLayout("crosstab");
	const { periodId, basis } = useAccountingContext();

	const mode = search.mode ?? "chess";
	const isChess = mode === "chess";
	const [dimensionsFilter, setDimensionsFilter] = useState<DimensionFilter>({});

	useBreadcrumbs([
		{
			label: isChess
				? t("chess_balance", { defaultValue: "Шахматная ведомость" })
				: t("journal_order", { defaultValue: "Журнал-ордер" }),
		},
	]);

	const { data, isLoading } = useGetCrossTabBalance(periodId, basis);

	// Account name lookup
	const { data: accountsData } = useListAccounts();
	const nameMap = useMemo(() => {
		const map = new Map<string, string>();
		for (const a of accountsData?.accounts ?? []) {
			map.set(a.code, a.name);
		}
		return map;
	}, [accountsData]);

	// Drill-down state
	const [drillDown, setDrillDown] = useState<{
		accountCode: string;
		title: string;
	} | null>(null);

	// Build sparse matrix into row data
	const { rows, columnDefs, pinnedBottom } = useMemo(() => {
		if (!data) return { rows: [], columnDefs: [], pinnedBottom: [] };

		// In chess mode: rows = debit accounts, columns = credit accounts
		// In journal-order mode: rows = credit accounts, columns = debit accounts (transpose)
		const rowAccounts = isChess
			? data.debitAccounts
			: data.creditAccounts;
		const colAccounts = isChess
			? data.creditAccounts
			: data.debitAccounts;
		const rowTotals = isChess ? data.rowTotals : data.colTotals;
		const colTotals = isChess ? data.colTotals : data.rowTotals;

		// Build cell lookup
		const cellMap = new Map<string, number>();
		for (const cell of data.cells) {
			const key = isChess
				? `${cell.debitAccount}:${cell.creditAccount}`
				: `${cell.creditAccount}:${cell.debitAccount}`;
			cellMap.set(key, Number.parseFloat(cell.amount || "0"));
		}

		// Row data
		const rows: CrossTabRow[] = rowAccounts.map((rowAcc) => {
			const row: CrossTabRow = {
				_account: rowAcc,
				_accountName: nameMap.get(rowAcc) ?? "",
				_total: Number.parseFloat(rowTotals[rowAcc] || "0"),
			};
			for (const colAcc of colAccounts) {
				const val = cellMap.get(`${rowAcc}:${colAcc}`);
				if (val) row[colAcc] = val;
			}
			return row;
		});

		// Pinned bottom (totals)
		const totalRow: CrossTabRow = {
			_account: t("total"),
			_accountName: "",
			_total: Object.values(rowTotals).reduce(
				(s, v) => s + Number.parseFloat(v || "0"),
				0,
			),
		};
		for (const colAcc of colAccounts) {
			const val = Number.parseFloat(colTotals[colAcc] || "0");
			if (val) totalRow[colAcc] = val;
		}

		const amountFmt = (p: ValueFormatterParams) =>
			p.value ? formatAmount(p.value) : "";

		// Column defs
		const colDefs: ColDef[] = [
			{
				field: "_account",
				headerName: isChess
					? t("line_debit")
					: t("line_credit"),
				width: 80,
				pinned: "left" as const,
				cellClass: "font-mono font-medium",
			},
			{
				field: "_accountName",
				headerName: t("account_name"),
				width: 160,
				pinned: "left" as const,
			},
			...colAccounts.map(
				(colAcc): ColDef => ({
					field: colAcc,
					headerName: colAcc,
					headerTooltip: `${colAcc} ${nameMap.get(colAcc) ?? ""}`,
					width: 110,
					type: "rightAligned",
					cellClass: "font-tabular",
					valueFormatter: amountFmt,
				}),
			),
			{
				field: "_total",
				headerName: t("total"),
				width: 130,
				pinned: "right" as const,
				type: "rightAligned",
				cellClass: "font-tabular font-medium",
				valueFormatter: amountFmt,
			},
		];

		return { rows, columnDefs: colDefs, pinnedBottom: [totalRow] };
	}, [data, isChess, nameMap, t]);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			filter: true,
			filterParams: {
				buttons: ["apply", "reset"],
				closeOnApply: true,
			},
		}),
		[],
	);

	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "period",
				content: `${t("period")}: ${periodId}`,
				align: "left" as const,
			},
			{
				id: "size",
				content: `${data?.debitAccounts.length ?? 0} × ${data?.creditAccounts.length ?? 0}`,
				align: "left" as const,
			},
		],
		[periodId, data, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<div className="flex flex-col h-full overflow-hidden">
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("search_placeholder", { defaultValue: "Поиск..." })}
						onChange={(e) =>
							gridRef.current?.api?.setGridOption(
								"quickFilterText",
								e.target.value,
							)
						}
						className="pl-9 h-9"
					/>
				</div>
				<Button
					variant={isChess ? "default" : "outline"}
					size="sm"
					className="h-9"
				>
					{t("chess_balance", { defaultValue: "Шахматка" })}
				</Button>
				<Button
					variant={!isChess ? "default" : "outline"}
					size="sm"
					className="h-9"
				>
					{t("journal_order", { defaultValue: "Журнал-ордер" })}
				</Button>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() =>
						gridRef.current?.api?.exportDataAsCsv({
							fileName: `${mode}_${periodId}.csv`,
						})
					}
				>
					CSV
				</Button>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => window.print()}
				>
					{t("toolbar_print")}
				</Button>
			</div>
			<DimensionFilterBar value={dimensionsFilter} onChange={setDimensionsFilter} />

			<DrillDownDialog
				open={!!drillDown}
				onOpenChange={(open) => {
					if (!open) setDrillDown(null);
				}}
				accountCode={drillDown?.accountCode}
				periodId={periodId}
				title={drillDown?.title}
			/>

			<div
				className="flex-1 ag-theme-quartz"
				style={{ width: "100%" }}
			>
				<AgGridReact
					ref={gridRef}
					rowData={rows}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					localeText={AG_GRID_LOCALE_RU}
					onGridReady={onGridReady}
					onSortChanged={onStateChanged}
					onFilterChanged={onStateChanged}
					onColumnMoved={onStateChanged}
					onColumnResized={onStateChanged}
					onColumnVisible={onStateChanged}
					rowHeight={28}
					headerHeight={32}
					animateRows={false}
					getRowId={(p) => String(p.data._account)}
					loading={isLoading}
					pinnedBottomRowData={pinnedBottom}
					overlayNoRowsTemplate={`<div style="text-align:center;padding:40px"><p style="font-size:13px;font-weight:500">${t("no_data_for_period")}</p></div>`}
				/>
			</div>
		</div>
	);
}
